export interface ArticleSection {
  type: "heading" | "paragraph" | "quote" | "list" | "divider";
  content?: string;
  items?: string[];
}

export interface Article {
  id: string;
  title: string;
  subtitle: string;
  tag: string;
  tagColor: string;
  tagBg: string;
  readTime: string;
  date: string;
  coverImg: string;
  summary: string;
  sections: ArticleSection[];
  relatedIds: string[];
}

export const articles: Article[] = [
  {
    id: "what-is-dzi",
    title: "天珠是什麼？天珠的文化與歷史",
    subtitle: "深入了解西藏天珠的起源、文化意涵與千年傳承",
    tag: "天珠文化",
    tagColor: "#8b6f47",
    tagBg: "#f5ede0",
    readTime: "5 分鐘閱讀",
    date: "2026.03.15",
    coverImg:
      "https://readdy.ai/api/search-image?query=collection%20of%20ancient%20Tibetan%20dzi%20beads%20various%20patterns%20nine%20eye%20three%20eye%20arranged%20on%20aged%20wooden%20surface%20warm%20golden%20atmospheric%20light%20cultural%20heritage%20documentary%20photography%20high%20detail%20texture&width=1200&height=600&seq=art_dzi_cover&orientation=landscape",
    summary:
      "天珠，藏語稱「gZi」，是藏傳文化中最神聖的護符之一。這顆來自高原的神秘寶石，歷經千年流傳，至今仍被視為具有強大能量與守護力量的珍貴飾品。",
    sections: [
      {
        type: "heading",
        content: "天珠的起源與傳說",
      },
      {
        type: "paragraph",
        content:
          "天珠，藏語稱「gZi」（音：「子」），是藏傳文化中最神聖的護符之一。關於天珠的起源，藏族民間流傳著許多神秘的傳說。其中最廣為人知的說法是：天珠並非人工製造，而是天神的飾品，因不慎墜落人間而成為凡人所得的寶物。",
      },
      {
        type: "paragraph",
        content:
          "另一種說法則認為，天珠是一種生長在西藏高原的神奇蟲子，這種蟲子在被人類觸碰後便會石化，形成天珠。這些傳說雖然充滿神話色彩，卻反映了藏族人民對天珠的崇敬與珍視。",
      },
      {
        type: "quote",
        content:
          "天珠是藏族人民心中最珍貴的寶物，它不只是一件飾品，更是一份來自天地的祝福與守護。",
      },
      {
        type: "heading",
        content: "天珠的材質與製作",
      },
      {
        type: "paragraph",
        content:
          "從考古學與礦物學的角度來看，天珠是一種以瑪瑙（Agate）為主要材質的古代珠子，表面經過特殊的化學處理，形成獨特的白色紋路與圖案。這種製作工藝在西元前一千年左右的中亞地區已有記載，被稱為「蝕花瑪瑙」技術。",
      },
      {
        type: "paragraph",
        content:
          "真正的古代天珠（老天珠）因年代久遠，表面會形成自然的風化紋路，稱為「皮殼」，這是鑑別天珠真偽的重要依據之一。老天珠的數量極為稀少，因此價值極高，是收藏家夢寐以求的珍品。",
      },
      {
        type: "heading",
        content: "天珠的眼數與意義",
      },
      {
        type: "paragraph",
        content:
          "天珠最重要的特徵是其表面的「眼」紋，不同眼數的天珠代表不同的能量與寓意：",
      },
      {
        type: "list",
        items: [
          "一眼天珠：象徵智慧之眼，能開啟智慧、增強洞察力",
          "三眼天珠：代表福、祿、壽三大能量，象徵事業、財富與健康",
          "五眼天珠：象徵五方佛的加持，帶來全方位的守護",
          "七眼天珠：代表七種福報，象徵圓滿與吉祥",
          "九眼天珠：象徵至尊能量，寓意財富、智慧與貴人相助，為天珠中最具代表性的守護之珠",
          "虎牙天珠：象徵勇氣與守護，被認為具有化解負能量的寓意",
          "蓮花天珠：象徵純潔與智慧，寓意心靈的昇華",
        ],
      },
      {
        type: "heading",
        content: "如何辨別天珠的真偽",
      },
      {
        type: "paragraph",
        content:
          "市面上天珠的真偽問題一直是購買者最關心的議題。以下是幾個辨別天珠真偽的基本方法：",
      },
      {
        type: "list",
        items: [
          "觀察皮殼：老天珠表面會有自然形成的風化層，稱為「皮殼」，質感溫潤，非人工可仿製",
          "感受重量：真正的瑪瑙天珠有一定的重量感，過輕者可能為塑料或玻璃仿製品",
          "觀察紋路：真天珠的白色紋路是滲入瑪瑙內部的，而非表面塗層",
          "觸感溫潤：真正的天珠觸感溫潤，有「入手即暖」的感覺",
          "尋找信譽商家：最重要的是向有信譽、有專業知識的商家購買",
        ],
      },
      {
        type: "quote",
        content:
          "每一顆天珠都有它的緣分，當你與它相遇的那一刻，你便能感受到那份獨特的能量共鳴。",
      },
      {
        type: "heading",
        content: "天珠在現代生活中的意義",
      },
      {
        type: "paragraph",
        content:
          "在現代社會中，天珠不只是一件古老的文化遺產，更是許多人日常生活中的精神寄託。許多人佩戴天珠，不只是為了它的美觀，更是為了感受那份來自千年傳承的能量與祝福。",
      },
      {
        type: "paragraph",
        content:
          "無論你是否相信天珠的神秘力量，它所承載的東方文化智慧與美學價值，都是值得我們珍視與傳承的。在瓏靖，我們致力於讓每一位有緣人都能找到屬於自己的天珠，感受那份跨越千年的守護與祝福。",
      },
    ],
    relatedIds: ["how-to-choose-crystal", "jade-symbolism"],
  },
  {
    id: "how-to-choose-crystal",
    title: "如何挑選適合自己的水晶",
    subtitle: "從能量屬性、個人需求到直覺感應，找到與你共鳴的水晶夥伴",
    tag: "水晶指南",
    tagColor: "#7a6a9a",
    tagBg: "#f0edf7",
    readTime: "7 分鐘閱讀",
    date: "2026.02.28",
    coverImg:
      "https://readdy.ai/api/search-image?query=various%20natural%20crystals%20gemstones%20amethyst%20clear%20quartz%20rose%20quartz%20obsidian%20arranged%20beautifully%20on%20white%20marble%20surface%20soft%20natural%20light%20elegant%20minimalist%20lifestyle%20photography%20high%20detail&width=1200&height=600&seq=art_cry_cover&orientation=landscape",
    summary:
      "水晶的世界豐富而多彩，每一種水晶都有其獨特的能量頻率與象徵意義。如何在眾多水晶中找到最適合自己的那一顆？本文將從能量屬性、個人需求與直覺感應三個面向，帶你找到屬於你的水晶夥伴。",
    sections: [
      {
        type: "heading",
        content: "認識水晶的能量屬性",
      },
      {
        type: "paragraph",
        content:
          "每一種水晶都有其獨特的礦物結構與化學成分，這些物理特性決定了水晶的能量頻率。在能量學的觀點中，不同的水晶對應著不同的能量中心（脈輪），能夠影響我們身心靈的不同面向。",
      },
      {
        type: "paragraph",
        content:
          "了解常見水晶的能量屬性，是挑選水晶的第一步。以下是幾種最常見水晶的能量特性：",
      },
      {
        type: "list",
        items: [
          "紫水晶：對應頂輪與眉心輪，象徵智慧、靈性與直覺，有助於冥想與靈性提升",
          "白水晶：被稱為「能量之王」，具有淨化、放大與平衡能量的特質，是最萬用的水晶",
          "粉水晶：對應心輪，象徵愛情、溫柔與自我療癒，有助於吸引愛情能量",
          "黑曜石：強力的守護石，能夠吸收並轉化負能量，為佩戴者建立能量防護屏障",
          "金髮晶：象徵財富、事業與正能量，是招財旺運的首選水晶",
          "青金石：象徵智慧與真理，有助於提升溝通能力與自我表達",
        ],
      },
      {
        type: "heading",
        content: "根據個人需求選擇水晶",
      },
      {
        type: "paragraph",
        content:
          "在了解水晶的基本能量屬性後，下一步是根據自己目前的需求來選擇。以下是幾個常見的需求方向與對應的水晶建議：",
      },
      {
        type: "list",
        items: [
          "需要財富與事業運：金髮晶、黃水晶、虎眼石",
          "需要愛情與人際關係：粉水晶、月光石、草莓晶",
          "需要心靈平靜與減壓：紫水晶、藍紋瑪瑙、海藍寶",
          "需要守護與能量防護：黑曜石、黑碧璽、煙晶",
          "需要健康與活力：紅碧玉、石榴石、紅瑪瑙",
          "需要智慧與學習：青金石、螢石、白水晶",
        ],
      },
      {
        type: "quote",
        content:
          "最好的水晶，不一定是最貴的，而是那顆讓你一眼就心動、拿在手上就感到安心的那一顆。",
      },
      {
        type: "heading",
        content: "相信直覺的力量",
      },
      {
        type: "paragraph",
        content:
          "除了理性地分析能量屬性與個人需求，直覺也是挑選水晶的重要依據。許多水晶愛好者都有這樣的經驗：當你走進一家水晶店，某顆水晶會莫名地吸引你的目光，讓你忍不住想要拿起來。這種直覺式的吸引，往往是你的潛意識在告訴你，這顆水晶正是你目前所需要的。",
      },
      {
        type: "paragraph",
        content:
          "在挑選水晶時，可以試著閉上眼睛，感受手中水晶的能量。有些人會感受到溫熱感、輕微的震動，或是一種莫名的安心感。這些都是水晶與你產生能量共鳴的訊號。",
      },
      {
        type: "heading",
        content: "水晶的淨化與保養",
      },
      {
        type: "paragraph",
        content:
          "購買水晶後，第一件事是進行淨化。水晶在到達你手中之前，可能已經接觸過許多不同的能量，淨化可以清除這些雜質能量，讓水晶以最純淨的狀態與你相遇。",
      },
      {
        type: "list",
        items: [
          "月光淨化：在滿月夜晚，將水晶放在窗台或戶外，讓月光照射一整夜",
          "陽光淨化：將水晶放在陽光下照射數小時（注意：部分水晶如紫水晶、粉水晶不適合長時間日曬）",
          "鹽水淨化：將水晶浸泡在海鹽水中數小時（注意：部分水晶不適合接觸水）",
          "煙燻淨化：用鼠尾草或聖木的煙霧繞繞水晶，進行能量淨化",
          "音頻淨化：用頌缽或音叉的聲波振動來淨化水晶",
        ],
      },
      {
        type: "heading",
        content: "與水晶建立連結",
      },
      {
        type: "paragraph",
        content:
          "淨化完成後，可以透過冥想或設定意圖的方式，與水晶建立個人連結。將水晶握在手中，閉上眼睛，在心中設定你希望這顆水晶為你帶來的能量與祝福。這個過程，就是將你的意圖注入水晶的過程。",
      },
      {
        type: "paragraph",
        content:
          "記住，水晶是你能量旅程的夥伴，而不是萬能的解決方案。最重要的，是你自己對生活的態度與行動。水晶能做的，是在你前行的路上，給予你一份溫柔的支持與陪伴。",
      },
    ],
    relatedIds: ["what-is-dzi", "jade-symbolism"],
  },
  {
    id: "jade-symbolism",
    title: "翡翠玉石的象徵意義",
    subtitle: "探索翡翠在東方文化中的深厚意涵與吉祥寓意",
    tag: "玉石文化",
    tagColor: "#4a7a52",
    tagBg: "#eaf2eb",
    readTime: "6 分鐘閱讀",
    date: "2026.02.10",
    coverImg:
      "https://readdy.ai/api/search-image?query=jade%20jadeite%20green%20jewelry%20collection%20bracelet%20pendant%20bangle%20on%20white%20marble%20with%20green%20leaves%20soft%20natural%20light%20elegant%20oriental%20style%20high%20detail%20texture%20cultural%20heritage&width=1200&height=600&seq=art_jade_cover&orientation=landscape",
    summary:
      "「君子比德於玉」——玉石在中國文化中的地位，遠超過一般的寶石。它不只是美麗的裝飾品，更是品德、文化與精神的象徵。本文帶你深入了解翡翠玉石在東方文化中的豐富意涵。",
    sections: [
      {
        type: "heading",
        content: "玉石在中國文化中的地位",
      },
      {
        type: "paragraph",
        content:
          "在中國文化中，玉石的地位是任何其他寶石都無法比擬的。早在新石器時代，中國人就已開始使用玉石製作禮器與飾品。孔子曾說「君子比德於玉」，將玉石的特質與君子的品德相比擬，賦予了玉石深厚的文化意涵。",
      },
      {
        type: "paragraph",
        content:
          "玉石在中國文化中象徵著五種美德：仁（溫潤如玉）、義（廉而不劌）、智（其聲舒揚）、勇（折而不撓）、潔（瑕不掩瑜）。這五種美德，正是中國傳統文化中對君子人格的最高期許。",
      },
      {
        type: "quote",
        content:
          "玉不琢，不成器。人不學，不知道。——《禮記·學記》",
      },
      {
        type: "heading",
        content: "翡翠的種類與特色",
      },
      {
        type: "paragraph",
        content:
          "翡翠（Jadeite）是玉石中最珍貴的一種，主要產自緬甸。翡翠的顏色豐富多彩，從深綠到淺綠，從白色到紫色，每一種顏色都有其獨特的美感與寓意：",
      },
      {
        type: "list",
        items: [
          "帝王綠：最珍貴的翡翠顏色，深邃濃郁的綠色，象徵尊貴與財富",
          "陽綠：鮮亮的翠綠色，充滿生機與活力，象徵事業蓬勃",
          "冰種：質地通透如冰，象徵純潔與高雅",
          "白色翡翠：象徵純潔與吉祥，是送禮的好選擇",
          "紫色翡翠（春彩）：象徵靈性與智慧，較為稀有珍貴",
          "黃色翡翠：象徵財富與福氣，寓意黃金滿屋",
        ],
      },
      {
        type: "heading",
        content: "常見玉石造型的吉祥寓意",
      },
      {
        type: "paragraph",
        content:
          "除了顏色，玉石的造型也承載著豐富的文化寓意。以下是幾種最常見的玉石造型及其象徵意義：",
      },
      {
        type: "list",
        items: [
          "平安扣：圓形象徵圓滿與平安，是最經典的玉石造型，寓意一切平安順遂",
          "觀音：慈悲守護，象徵心靈的平靜與庇佑，適合女性佩戴",
          "佛公（彌勒佛）：象徵歡喜與福氣，寓意笑口常開、福氣滿滿，適合男性佩戴",
          "如意：象徵萬事如意、心想事成，是送禮的吉祥之選",
          "葉形：象徵生命力與事業的蓬勃發展，寓意「事業有成」",
          "龍鳳：象徵吉祥如意，龍鳳呈祥，適合新婚夫妻",
          "魚形：「魚」與「餘」諧音，象徵年年有餘、財富豐盛",
        ],
      },
      {
        type: "heading",
        content: "如何辨別翡翠的品質",
      },
      {
        type: "paragraph",
        content:
          "翡翠的品質評估主要從「種、水、色、工」四個面向來判斷：",
      },
      {
        type: "list",
        items: [
          "種（質地）：指翡翠的結晶結構，種越老（結晶越細密），品質越高",
          "水（透明度）：指翡翠的透明程度，透明度越高，品質越好",
          "色（顏色）：指翡翠的顏色，以帝王綠為最高等級，顏色要均勻、鮮豔",
          "工（工藝）：指翡翠的雕刻工藝，工藝越精細，附加價值越高",
        ],
      },
      {
        type: "quote",
        content:
          "玉石的美，不在於它的外表，而在於它所承載的文化意涵與精神價值。每一件玉石，都是大自然與人類智慧共同創造的藝術品。",
      },
      {
        type: "heading",
        content: "玉石的保養與傳承",
      },
      {
        type: "paragraph",
        content:
          "玉石需要細心保養，才能保持其溫潤的光澤。日常保養的要點包括：避免碰撞與刮傷、避免接觸化學物質（如香水、清潔劑）、定期用柔軟的布輕輕擦拭，以及避免長時間暴露在強烈陽光下。",
      },
      {
        type: "paragraph",
        content:
          "在中國傳統文化中，玉石是可以傳承的。一件祖傳的玉石，不只承載著家族的記憶，更蘊含著代代相傳的祝福與守護。這種傳承的意義，讓玉石超越了一般飾品的價值，成為連結過去與未來的橋樑。",
      },
    ],
    relatedIds: ["what-is-dzi", "thangka-art-value"],
  },
  {
    id: "thangka-art-value",
    title: "唐卡藝術的文化價值",
    subtitle: "走進唐卡藝術的世界，了解這門古老藝術的創作過程與收藏意義",
    tag: "藝術文化",
    tagColor: "#c9a96e",
    tagBg: "#faf5ec",
    readTime: "8 分鐘閱讀",
    date: "2026.01.20",
    coverImg:
      "https://readdy.ai/api/search-image?query=Tibetan%20thangka%20painting%20detail%20Buddhist%20art%20colorful%20mandala%20lotus%20deity%20traditional%20artwork%20close%20up%20warm%20golden%20light%20cultural%20heritage%20photography%20high%20detail%20artistic&width=1200&height=600&seq=art_thk_cover&orientation=landscape",
    summary:
      "唐卡，藏語意為「卷軸畫」，是藏傳佛教藝術中最重要的表現形式之一。每一幅唐卡都是藝術家以虔誠之心，歷經數月甚至數年精心繪製而成，蘊含著深厚的宗教意涵與藝術價值。",
    sections: [
      {
        type: "heading",
        content: "唐卡的起源與歷史",
      },
      {
        type: "paragraph",
        content:
          "唐卡的歷史可以追溯到西元七世紀，隨著佛教傳入西藏而逐漸發展成熟。最初，唐卡是作為宗教教育的工具，用來向不識字的信眾傳達佛教的教義與故事。隨著時間的推移，唐卡逐漸發展成為一門獨立的藝術形式，融合了印度、尼泊爾與中國的藝術風格，形成了獨特的藏傳佛教藝術語言。",
      },
      {
        type: "paragraph",
        content:
          "唐卡的製作傳統在西藏各地形成了不同的流派，其中最著名的包括：衛藏畫派（以拉薩為中心）、勉唐畫派（以日喀則為中心）、欽孜畫派（以江孜為中心）等。每個畫派都有其獨特的風格與技法，但都遵循著嚴格的宗教規範與藝術傳統。",
      },
      {
        type: "quote",
        content:
          "每一幅唐卡都是一扇通往神聖世界的窗，透過它，我們得以一窺藏傳佛教的宇宙觀與精神世界。",
      },
      {
        type: "heading",
        content: "唐卡的創作過程",
      },
      {
        type: "paragraph",
        content:
          "唐卡的創作是一個充滿儀式感的過程，從準備材料到完成作品，每一個步驟都有其宗教意涵：",
      },
      {
        type: "list",
        items: [
          "準備畫布：傳統唐卡使用棉布或絲綢作為畫布，需要先進行特殊處理，使其表面平滑",
          "打底稿：藝術家根據嚴格的宗教規範，用鉛筆或炭筆打出底稿，每一個比例都有精確的規定",
          "上色：傳統唐卡使用天然礦物顏料，如青金石（藍色）、朱砂（紅色）、孔雀石（綠色）等，這些顏料歷經千年不褪色",
          "描金：在重要的部位用金粉描繪，增添神聖感與光輝",
          "開眼：最後一道工序，由高僧為唐卡中的神像「開眼」，賦予其神聖的力量",
          "裝裱：完成的唐卡會用絲綢裝裱，製作成可以卷起收藏的卷軸形式",
        ],
      },
      {
        type: "heading",
        content: "唐卡的主要題材",
      },
      {
        type: "paragraph",
        content:
          "唐卡的題材非常豐富，主要包括以下幾類：",
      },
      {
        type: "list",
        items: [
          "佛陀與菩薩：如釋迦牟尼佛、觀世音菩薩、文殊菩薩等，是最常見的唐卡題材",
          "護法神：如大黑天、吉祥天母等，象徵守護與降魔",
          "曼陀羅：象徵宇宙的幾何圖形，用於冥想與修行",
          "度母：包括綠度母與白度母，象徵慈悲與守護",
          "歷史人物：如蓮花生大士、宗喀巴大師等藏傳佛教的重要祖師",
          "生死輪迴圖：描繪六道輪迴的宇宙觀，是重要的宗教教育工具",
        ],
      },
      {
        type: "heading",
        content: "唐卡的收藏價值",
      },
      {
        type: "paragraph",
        content:
          "近年來，隨著人們對藏傳文化的興趣日益增加，唐卡的收藏價值也不斷提升。一幅高品質的手繪唐卡，不只具有宗教意義，更是一件珍貴的藝術品。評估唐卡收藏價值的主要因素包括：",
      },
      {
        type: "list",
        items: [
          "年代：古代唐卡（百年以上）的收藏價值遠高於現代作品",
          "畫師：知名畫師的作品具有更高的藝術與收藏價值",
          "題材：稀有題材或特殊尺寸的唐卡更具收藏價值",
          "保存狀況：保存完好、顏色鮮豔的唐卡價值更高",
          "材料：使用天然礦物顏料與金粉的唐卡品質更佳",
        ],
      },
      {
        type: "quote",
        content:
          "收藏一幅唐卡，不只是擁有一件藝術品，更是與一段歷史、一種文化、一份信仰建立了深刻的連結。",
      },
      {
        type: "heading",
        content: "唐卡在現代生活中的意義",
      },
      {
        type: "paragraph",
        content:
          "在現代社會中，唐卡不只是宗教場所的裝飾品，越來越多的人將唐卡帶入日常生活空間。一幅精美的唐卡，能夠為居家環境帶來一份寧靜與莊嚴的氛圍，也是對東方文化的一種尊重與傳承。",
      },
      {
        type: "paragraph",
        content:
          "對於許多人來說，唐卡更是一種心靈的寄託。在繁忙的現代生活中，凝視一幅唐卡，感受其中蘊含的寧靜與智慧，是一種簡單而有效的心靈療癒方式。瓏靖精選的每一幅唐卡，都是由具有豐富經驗的藏族藝術家手工繪製，承載著真實的文化傳承與藝術價值。",
      },
    ],
    relatedIds: ["what-is-dzi", "jade-symbolism"],
  },
];
